<template>
	<section class="deal-checklist-view-container stretch-to-fit">
		<LoaderComponent v-show="isLoading" />

		<div
			:class="{
				'deal-checklist-view': true,
				'stretch-to-fit': true,
				hidden: isLoading,
			}"
		>
			<div
				v-show="!showDeleteScreen"
				class="item-info"
			>
				<h1>{{ checklistItem.name }}</h1>

				<table class="item-info-metadata">
					<tbody>
						<tr>
							<th><b>Completed:</b></th>
							<td>
								<p>{{ timestampFormatted }}</p>
							</td>
						</tr>
						<tr>
							<th><b>Status:</b></th>
							<td>
								<p>
									<span :style="{ backgroundColor: statusColor }">{{ checklistItem.getOverallStatus() }}</span>
								</p>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<hr style="margin: 10px" />

			<div
				v-if="showDeleteScreen"
				class="item-delete-screen item-content stretch-to-fit"
			>
				<div class="delete-header">
					<h2>Are you sure you want to delete this document?</h2>
					<p>This action cannot be undone.</p>
				</div>

				<div class="button-slant-split button-group">
					<button
						class="button-inactive"
						@click="showDeleteScreen = false"
					>
						Cancel
					</button>
					<button
						class="button-delete"
						@click="onDeleteDocument"
					>
						Delete
					</button>
				</div>
			</div>

			<div
				v-else-if="checklistItem.isTypeUpload()"
				class="item-content stretch-to-fit"
			>
				<div>
					<p
						v-if="imageLoadingError"
						class="warning-message"
					>
						<i class="fas fa-info-circle" /> We were unable to load your image.
					</p>

					<div class="item-actions button-slant-split">
						<button
							class="button-download"
							@click="onDownloadDocument"
						>
							<i class="fas fa-download" />
							Download
						</button>
						<button
							class="button-delete"
							:disabled="editingLocked"
							@click="showDeleteScreen = true"
						>
							<i class="fas fa-trash" />
							Delete
						</button>
					</div>

					<DocumentPreview
						class="upload-preview"
						:document="document"
						hideCaption
						@load="onImageLoad(false)"
						@error="onImageLoad(true)"
					/>
				</div>

				<button
					class="button-start"
					@click="onUpdateItem(checklistItem.id)"
					:disabled="checklistItem.status.isVerified"
				>
					<i class="fas fa-undo" />
					Reupload
				</button>
			</div>

			<div
				v-else-if="checklistItem.isTypePlaid()"
				class="item-content plaid-preview"
			>
				<!--<CustomerChecklistViewPlaid :plaidData="plaidData" :plaidInteraction="plaidData.interactionType" />-->
			</div>

			<div
				v-else
				class="item-content"
			>
				<p class="warning-message">
					<i class="fas fa-info-circle" />
					Error: Unsupported item type. Unable to display any information.
				</p>
			</div>
		</div>
	</section>
</template>

<script>
	import api from '@core/services/api/_api';
	import settings from 'settings';
	import ENUMS from '@core/classes/Enums';
	import DealChecklistItem from '@core/classes/Checklist/DealChecklistItem';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import DocumentPreview from '@core/components/DocumentPreview.vue';
	//import CustomerChecklistViewPlaid from '@/components/customerChecklist/CustomerChecklistViewPlaid';

	export default {
		name: 'CustomerChecklistViewItem',
		emits: ['deleteComplete', 'error'],
		props: {
			dealId: String,
			dealRole: Number, // ENUMS.PERSON_TYPES
			checklistItem: DealChecklistItem,
			onUpdateItem: Function,
		},
		data() {
			return {
				isLoading: false,
				uploadSrc: null,
				imageLoadingError: false,
				showDeleteScreen: false,
			};
		},
		computed: {
			document() {
				return this.checklistItem.getDocument();
			},
			plaidData() {
				return this.checklistItem.getPlaidData();
			},
			timestampFormatted() {
				return new Date(this.checklistItem.getTimestamp()).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
			},
			statusColor() {
				const status = this.checklistItem.getOverallStatus();
				if (status === 'APPROVED') return 'var(--success-button-color)';
				if (status === 'PENDING') return 'var(--print-color)';
				return 'var(--error-button-color)';
			},
			relevantPlaidData() {
				if (this.checklistItem.type == ENUMS.DealChecklistType.LaunchPlaid_IDV) return this.plaidData.identityVerification.response.user;
				if (this.checklistItem.type === ENUMS.DealChecklistType.LaunchPlaid_BankIncome)
					return this.plaidData.bankIncome?.response.bankIncome[0].items ?? this.plaidData;

				return {};
			},
			editingLocked() {
				const uploadDate = new Date(this.document.docInfo.timestamp);
				const timeDiff = (Date.now() - uploadDate) / 60000;

				return this.checklistItem.status.isVerified || timeDiff > 60;
			},
		},
		async created() {
			if (this.checklistItem.isDocTypeImage()) {
				this.isLoading = true;
				this.uploadSrc = `${settings.apiUrl}/Documents/Download/${this.document.id}`;
			}
		},
		methods: {
			onImageLoad(anyErrors) {
				this.imageLoadingError = anyErrors;
				this.isLoading = false;
			},
			async onDownloadDocument() {
				this.isLoading = true;
				await api.documents.downloadDocument(this.document.id, this.document.docInfo.fileName);
				this.isLoading = false;
			},
			async onDeleteDocument() {
				this.isLoading = true;

				// Double check that document hasn't been verified yet.
				const updatedItemResponse = await api.checklist.customer_getUpdatedItem(this.dealId, this.checklistItem.id, this.dealRole, this.checklistItem.setIndex);
				const updatedItem = updatedItemResponse?.data;

				if (!updatedItem) {
					this.$emit('error', 'Unable to carry out deletion request.');
				} else if (updatedItem.status.isVerified) {
					this.$emit('error', 'Item has already been manually verified. Cannot carry out deletion request.');
				} else {
					const response = await api.checklist.resetItem(this.checklistItem.status);
					if (response && response.data) this.$emit('deleteComplete');
				}

				this.showDeleteScreen = false;
				this.isLoading = false;
			},
		},
		components: {
			LoaderComponent,
			DocumentPreview,
			//CustomerChecklistViewPlaid
		},
	};
</script>
<style>
	.deal-checklist-view-container {
		width: 100%;
		position: relative;
	}

	.deal-checklist-view-container .spinner-container {
		position: absolute;
	}

	.deal-checklist-view.hidden {
		visibility: hidden;
	}

	.deal-checklist-view .warning-message {
		width: fit-content;
		margin: auto;
	}

	.deal-checklist-view .item-info .item-info-metadata {
		width: 100%;
		margin-bottom: 10px;
		border-spacing: 0 10px;
		text-align: left;
	}

	.deal-checklist-view .item-info .item-info-metadata tr {
		margin-bottom: 10px 0;
	}

	.deal-checklist-view .item-info .item-info-metadata span {
		width: fit-content;
		padding: 5px 10px;
		border-radius: 10px;
		font-weight: bold;
	}

	.deal-checklist-view .item-content {
		margin-top: 10px;
	}

	.deal-checklist-view .item-content.stretch-to-fit {
		justify-content: space-between;
	}

	.deal-checklist-view .item-content button {
		width: 100%;
		padding: 10px;
		border: none;
		border-radius: 10px;
		color: var(--alt-text-color);
		font-size: 20px;
		font-weight: bold;
	}

	.deal-checklist-view .item-content button[disabled] {
		opacity: 0.5;
	}

	.deal-checklist-view .item-content button.button-start {
		margin-top: 50px;
		background-color: var(--link-alt-text-color) !important;
	}

	.deal-checklist-view .item-content .upload-preview {
		margin: 10px 0;
	}

	.deal-checklist-view .item-content .upload-preview .spinner-container {
		position: unset;
		display: unset;
		background-color: unset;
		z-index: unset;
	}

	.deal-checklist-view .item-content .item-actions {
		display: flex;
		justify-content: space-evenly;
	}

	.deal-checklist-view .item-content .item-actions button {
		background-color: var(--link-alt-text-color);
	}

	.deal-checklist-view .item-content .item-actions button.button-download {
		padding-right: 20px;
		filter: hue-rotate(180deg);
	}

	.deal-checklist-view .item-content .item-actions button.button-delete {
		padding-left: 20px;
		background-color: var(--error-button-color) !important;
	}

	.deal-checklist-view .item-delete-screen .delete-header {
		text-align: center;
	}

	.deal-checklist-view .item-delete-screen .delete-header p {
		margin: 10px 0;
	}

	.deal-checklist-view .item-delete-screen .button-group button {
		background-color: var(--error-button-color);
	}

	.deal-checklist-view .item-delete-screen .button-group button.button-inactive {
		filter: hue-rotate(180deg);
	}
</style>
